import { useCallback, useEffect, useState } from "react";
import { Gift, Copy, Share2, Check, Users, Flame, Trophy, Loader2, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReferralContestBanner from "@/components/dashboard/ReferralContestBanner";

const Stat = ({ label, value, accent, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-vts-line bg-vts-surface/40 p-3">
    <p className={`text-lg font-bold font-mono ${accent || "text-white"}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-wider text-vts-silver/50 mt-0.5">{label}</p>
  </div>
);

export const ReferralPanel = ({ accountId, onConnect }) => {
  const [stats, setStats] = useState(null);
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const requestPayout = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post("/referral/request-payout", { account_id: accountId });
      const msg = encodeURIComponent(`Hi! Requesting my VTS referral payout of $${Number(data.amount).toFixed(2)} (account ${accountId}).`);
      window.open(`https://t.me/${data.contact}?text=${msg}`, "_blank");
      toast.success(`Payout request sent — DM @${data.contact} to receive $${Number(data.amount).toFixed(2)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not request payout");
    } finally {
      setRequesting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        accountId ? api.get(`/referral/stats/${encodeURIComponent(accountId)}`) : Promise.resolve(null),
        api.get("/referral/leaderboard"),
      ]);
      if (s) setStats(s.data);
      setBoard(b.data?.leaders || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const link = stats?.ref_link?.startsWith("http")
    ? stats.ref_link
    : `${window.location.origin}${stats?.ref_link || ""}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Referral link copied — share it anywhere");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — long-press the link to copy manually");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Trade on VTS", text: "Trade Forex & synthetics with auto-bots on VTS 👇", url: link });
      } catch { /* user cancelled */ }
    } else copy();
  };

  return (
    <div id="referral-panel" data-testid="referral-panel" className="space-y-4 scroll-mt-24">
      <div className="flex items-center gap-2">
        <Gift className="h-5 w-5 text-amber-400" />
        <h2 className="text-lg font-bold text-white">Referral Program</h2>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-vts-silver/50" />}
      </div>

      <ReferralContestBanner accountId={accountId} />

      {!accountId ? (
        <div data-testid="referral-login-prompt" className="rounded-xl border border-vts-line bg-vts-surface/40 p-5 space-y-3">
          <p className="text-sm text-white font-semibold">Earn a share of the trading commission your friends generate.</p>
          <p className="text-xs text-vts-silver/60">
            Connect your Deriv account to get your personal referral link. When someone signs up through it and trades,
            you earn <b className="text-amber-400">10–20%</b> of the platform markup their trading generates — for as long as they trade.
          </p>
          <button data-testid="referral-connect-btn" onClick={onConnect}
            className="rounded-lg bg-vts-blue px-4 py-2 text-sm font-semibold text-white hover:bg-vts-blue/80 transition">
            Log in with Deriv to get your link
          </button>
        </div>
      ) : (
        <>
          {/* Link + copy */}
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Your referral link</p>
            <div className="flex items-center gap-2 flex-wrap">
              <code data-testid="referral-link" className="flex-1 min-w-[200px] truncate rounded-lg border border-vts-line bg-black/40 px-3 py-2 text-xs text-white font-mono">
                {link}
              </code>
              <button data-testid="referral-copy-btn" onClick={copy}
                className="flex items-center gap-1.5 rounded-lg border border-amber-500/50 bg-amber-500/15 px-3 py-2 text-xs font-semibold text-amber-400 hover:bg-amber-500/25 transition">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
              </button>
              <button data-testid="referral-share-btn" onClick={share}
                className="flex items-center gap-1.5 rounded-lg border border-vts-line px-3 py-2 text-xs font-semibold text-vts-silver/80 hover:text-white transition">
                <Share2 className="h-3.5 w-3.5" /> Share
              </button>
            </div>
            {!stats?.named && (
              <p className="text-[10px] text-vts-silver/50">
                Tip: set a nickname on the leaderboard to get a cleaner link (e.g. ?ref=YourName instead of your account id).
              </p>
            )}
          </div>

          {/* SHAREABLE INVITE CARD — one-tap spread */}
          <div data-testid="referral-invite-card" className="relative overflow-hidden rounded-xl border border-vts-blue/40 bg-gradient-to-br from-vts-blue/20 via-vts-surface to-amber-500/10 p-4 space-y-3">
            <div className="absolute -top-8 -right-8 h-28 w-28 rounded-full bg-vts-blue/20 blur-2xl pointer-events-none" />
            <p className="text-[10px] uppercase tracking-wider text-vts-blue font-bold">📣 Invite card — tap once, share anywhere</p>
            <div className="rounded-lg border border-vts-line bg-black/40 p-3 space-y-1">
              <p className="text-sm font-bold text-white">🤖 I trade with auto-bots on VTS</p>
              <p className="text-[11px] text-vts-silver/70 leading-snug">103-strategy Auto Bot + quick digit bots, running 24/7 on their servers — even with my phone off. Free $10,000 demo to try it. Join me 👇</p>
              <p className="text-[11px] font-mono text-amber-300 truncate">{link}</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <a data-testid="invite-share-telegram" target="_blank" rel="noreferrer"
                href={`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent("🤖 I trade with auto-bots on VTS — a 103-strategy Auto Bot + quick digit bots running 24/7 even with my phone off. Free $10k demo. Join me:")}`}
                className="flex-1 min-w-[100px] text-center rounded-lg bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/40 text-[11px] font-bold px-3 py-2 hover:brightness-125 transition">✈️ Telegram</a>
              <a data-testid="invite-share-whatsapp" target="_blank" rel="noreferrer"
                href={`https://wa.me/?text=${encodeURIComponent("🤖 I trade with auto-bots on VTS — running 24/7 even with my phone off. Free $10k demo to try it. Join me: " + link)}`}
                className="flex-1 min-w-[100px] text-center rounded-lg bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40 text-[11px] font-bold px-3 py-2 hover:brightness-125 transition">💬 WhatsApp</a>
              <a data-testid="invite-share-x" target="_blank" rel="noreferrer"
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("🤖 Trading with auto-bots on VTS — 24/7, phone off. Free $10k demo:")}&url=${encodeURIComponent(link)}`}
                className="flex-1 min-w-[100px] text-center rounded-lg bg-vts-surface text-white ring-1 ring-vts-line text-[11px] font-bold px-3 py-2 hover:brightness-125 transition">𝕏 Post</a>
              <button data-testid="invite-share-native" onClick={share}
                className="flex-1 min-w-[100px] rounded-lg bg-vts-blue text-white text-[11px] font-bold px-3 py-2 hover:bg-vts-blueHover transition">📲 More…</button>
            </div>
            <p className="text-[9px] text-vts-silver/45 font-mono">Every friend who trades pays you {stats?.share_pct ?? 10}–20% of their markup — forever. That&apos;s the whole pitch.</p>
          </div>

          {/* Earnings */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat testId="referral-stat-count" label="Referrals" value={stats?.referrals ?? 0} />
            <Stat testId="referral-stat-active" label="Active traders" value={stats?.active_referrals ?? 0} accent="text-vts-blue" />
            <Stat testId="referral-stat-share" label="Your share" value={`${stats?.share_pct ?? 10}%`} accent="text-amber-400" />
            <Stat testId="referral-stat-earn-month" label="Earned this month" value={`$${(stats?.earnings_month ?? 0).toFixed(2)}`} accent="text-vts-profit" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Stat testId="referral-stat-earn-all" label="Earned all-time" value={`$${(stats?.earnings_all ?? 0).toFixed(2)}`} accent="text-vts-profit" />
            <Stat testId="referral-stat-markup" label="Markup your referrals generated" value={`$${(stats?.markup_generated_all ?? 0).toFixed(2)}`} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Stat testId="referral-stat-paid" label="Paid to you" value={`$${(stats?.paid_all ?? 0).toFixed(2)}`} accent="text-vts-blue" />
            <Stat testId="referral-stat-pending" label="Pending payout" value={`$${(stats?.pending_payout ?? 0).toFixed(2)}`} accent="text-amber-400" />
          </div>

          {/* Request payout — unlocks at $20 pending */}
          <div data-testid="referral-request-box" className="rounded-xl border border-vts-line bg-vts-surface/40 p-4 flex items-center gap-3 flex-wrap">
            {stats?.open_request ? (
              <p data-testid="referral-request-open" className="text-xs text-vts-profit font-semibold">
                ✓ Payout requested — DM <a className="underline" href={`https://t.me/${stats?.payout_contact}`} target="_blank" rel="noreferrer">@{stats?.payout_contact}</a> on Telegram to receive it.
              </p>
            ) : (
              <>
                <button
                  data-testid="referral-request-btn"
                  disabled={requesting || (stats?.pending_payout ?? 0) < (stats?.payout_min ?? 20)}
                  onClick={requestPayout}
                  className="flex items-center gap-1.5 rounded-lg border border-vts-profit/50 bg-vts-profit/15 px-4 py-2 text-xs font-bold text-vts-profit hover:bg-vts-profit/25 transition disabled:opacity-40 disabled:cursor-not-allowed">
                  {requesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <DollarSign className="h-3.5 w-3.5" />}
                  Request payout
                </button>
                <p className="text-[10px] text-vts-silver/50">
                  {(stats?.pending_payout ?? 0) >= (stats?.payout_min ?? 20)
                    ? <>You'll be sent to Telegram to DM <b className="text-white">@{stats?.payout_contact}</b> — payouts are processed there.</>
                    : <>Unlocks at <b className="text-amber-400">${(stats?.payout_min ?? 20).toFixed(0)}</b> pending — you're at ${(stats?.pending_payout ?? 0).toFixed(2)}. Keep sharing your link!</>}
                </p>
              </>
            )}
          </div>

          {/* Payout history */}
          {(stats?.recent_payouts || []).length > 0 && (
            <div data-testid="referral-payout-history" className="rounded-xl border border-vts-line bg-vts-surface/40 p-4">
              <p className="text-[10px] uppercase tracking-wider text-vts-silver/50 font-bold mb-2">Payouts received</p>
              <div className="space-y-1.5">
                {stats.recent_payouts.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs font-mono">
                    <span className="text-vts-profit font-bold">+${Number(p.amount).toFixed(2)}</span>
                    <span className="text-vts-silver/50">{(p.paid_at || "").slice(0, 10)}</span>
                    {p.note && <span className="ml-auto text-vts-silver/45 truncate max-w-[180px]">{p.note}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tier ladder */}
          <div data-testid="referral-tiers" className="rounded-xl border border-vts-line bg-vts-surface/40 p-4">
            <p className="text-[10px] uppercase tracking-wider text-vts-silver/50 font-bold mb-2 flex items-center gap-1">
              <Flame className="h-3 w-3 text-amber-400" /> Commission tiers — more active referrals, bigger share
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              {(stats?.tiers || []).map((t) => {
                const active = (stats?.share_pct ?? 10) === t.share_pct;
                return (
                  <div key={t.share_pct} className={`rounded-lg border px-2 py-2 ${active ? "border-amber-400 bg-amber-500/10" : "border-vts-line"}`}>
                    <p className={`text-base font-bold font-mono ${active ? "text-amber-400" : "text-white"}`}>{t.share_pct}%</p>
                    <p className="text-[9px] text-vts-silver/50">{t.min_active}+ active</p>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-vts-silver/45 mt-2">
              You earn your share of the {stats?.markup_pct ?? 3}% platform markup on every REAL-money trade your
              referrals place. Demo accounts and practice/simulated trades earn nothing. Payouts are processed by the VTS team.
            </p>
          </div>

          {/* Referrals table */}
          {(stats?.rows || []).length > 0 && (
            <div data-testid="referral-rows" className="rounded-xl border border-vts-line bg-vts-surface/40 p-4">
              <p className="text-[10px] uppercase tracking-wider text-vts-silver/50 font-bold mb-2 flex items-center gap-1">
                <Users className="h-3 w-3" /> Your referrals
              </p>
              <div className="space-y-1.5">
                {stats.rows.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-white">{r.account}</span>
                    {r.is_demo && <span className="text-[8px] font-mono text-vts-silver/45 px-1 rounded bg-vts-line">DEMO</span>}
                    {r.active && <span className="text-[8px] font-mono text-vts-profit px-1 rounded bg-vts-profit/10">ACTIVE</span>}
                    <span className="ml-auto font-mono text-vts-silver/60">markup ${Number(r.markup_all || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Top referrers */}
      <div data-testid="referral-leaderboard" className="rounded-xl border border-vts-line bg-vts-surface/40 p-4">
        <p className="text-[10px] uppercase tracking-wider text-vts-silver/50 font-bold mb-2 flex items-center gap-1">
          <Trophy className="h-3 w-3 text-amber-400" /> Top referrers
        </p>
        {board.length === 0 ? (
          <p className="text-xs text-vts-silver/50">No referrals yet — be the first on the board. 🏆</p>
        ) : (
          <div className="space-y-1.5">
            {board.map((r) => (
              <div key={r.rank} className="flex items-center gap-2 text-xs">
                <span className="w-5 text-vts-silver/50 font-mono">#{r.rank}</span>
                <span className={`font-mono ${r.named ? "font-bold text-white" : "text-vts-silver/80"}`}>{r.account}</span>
                <span className="ml-auto font-mono text-vts-blue">{r.referrals} referral{r.referrals === 1 ? "" : "s"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralPanel;
